import { type ThemeProps } from '@kijiji/theme'
import { type TFunction } from 'i18next'
import { type StaticImageData } from 'next/image'

import { ATTRIBUTES } from '@/features/attributes/constants/attributes'
import { type VipQueryAttributeAll } from '@/types/listings'
import valueEstimateIcon from '~/public/next-assets/images/autocanada/il-dollar.png'
import warrantyShieldImg from '~/public/next-assets/images/autocanada/img-autocan-warranty-shield.png'

const valueEstimateMobileSrc = '/next-assets/images/autocanada/il-car-gold-bars.png'
const singleBadgeImage = '/next-assets/images/autocanada/il-estimate-96.png'
const valueEstimateInlineSrc = '/next-assets/images/autocanada/img-autocan-estimate.png'

export type AutocanFeatures = 'warranty' | 'value-estimate' | 'financing'
export type AutocanFeatureConfig = {
  /** Used as a BG color for the feature on desktop */
  bgColor: string
  /** Used for the BG color of the feature on mobile */
  bgColorLight: string
  /** It is the appended icon beside the card's image */
  icon: StaticImageData | string | null
  mobileIcon: StaticImageData | string | null
  /** Listing image to be displayed on the card if any*/
  listingImage?: StaticImageData | string
  /** Illustration to be displayed on the mobile card when no listing image is defined */
  mobileBadgeImage?: string
  /** Illustration to be shown on inline badge card when no listing image is defined  */
  inlineBadgeImage?: string
  /** Illustration to be shown when only one feature is available when no listing image is defined */
  singleBadgeImage?: string
  /**
   * Labels for the different card variations
   */
  labels: {
    link: string
    shortSubtitle: string
    shortTitle?: string
    subtitle: string
    title: string
  }
}

export const AUTOCAN_CONFIG_PER_FEATURE_TYPE = ({
  attributes,
  colors,
  mainImageSrc,
  t,
}: {
  t: TFunction
  colors: ThemeProps['colors']
  mainImageSrc: string
  /** Attributes used to populate labels */
  attributes: VipQueryAttributeAll
}): { [x in AutocanFeatures]: AutocanFeatureConfig } => {
  const { CAR_MAKE, CAR_MODEL, CAR_YEAR } = ATTRIBUTES

  const { carmake, carmodel, caryear } =
    attributes?.reduce((acc: Record<string, string>, curr) => {
      const name = curr?.canonicalName
      const value = curr?.values[0]

      if (!value) return acc

      if (name === CAR_MAKE || name === CAR_MODEL || name === CAR_YEAR) {
        return { ...acc, [name]: value }
      }

      return acc
    }, {}) ?? {}

  return {
    warranty: {
      bgColor: colors.purple.primary,
      bgColorLight: colors.purple.light5,
      listingImage: mainImageSrc,
      icon: warrantyShieldImg,
      mobileIcon: warrantyShieldImg,
      labels: {
        link: t('autocan.warranty.link.label'),
        shortTitle: t('autocan.warranty.title_short'),
        subtitle: t('autocan.warranty.subtitle_long', { carmake, carmodel, caryear }),
        shortSubtitle: t('autocan.warranty.subtitle_short'),
        title: t('autocan.warranty.title_long'),
      },
    },
    'value-estimate': {
      bgColor: colors.blue.primary,
      bgColorLight: colors.blue.light5,
      icon: null,
      inlineBadgeImage: valueEstimateInlineSrc,
      singleBadgeImage: singleBadgeImage,
      mobileBadgeImage: valueEstimateMobileSrc,
      mobileIcon: valueEstimateIcon,
      labels: {
        link: t('autocan.value_estimate.link.label'),
        subtitle: t('autocan.value_estimate.subtitle_long'),
        title: t('autocan.value_estimate.title'),
        shortSubtitle: t('autocan.value_estimate.link.subtitle_short'),
      },
    },
    financing: {
      bgColor: '',
      bgColorLight: '',
      icon: null,
      mobileIcon: null,
      labels: { link: '', subtitle: '', title: '', shortSubtitle: '' },
    },
  }
}
